/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LogNewShipmentForm from '../../../../common/commonComponents/logNewShipmentForm';
import { MediaSolutionService } from '../../../../../service/MediaSolutionServices';

interface PrepareSolutionProps {
  onSave: () => void;
  onCancel: () => void;
  showToastMessage: (message: string, type: string) => void;
}

interface ProductInfo {
  id: string;
  name: string;
  manufacturer: string;
  sku: string;
}


const PrepareSolution: React.FC<PrepareSolutionProps> = ({ onSave, onCancel, showToastMessage, }) => {
  const [productData, setProductData] = useState({ manufacturer: '', sku: '' });
  const [formData, setFormData] = useState({
    selectedProductId: '',
    lotId: '',
    manufactureDate: '',
    expiryDate: '',
    receivedGms: '',
    numContainers: '',
    qtyPerContainer: '0.00',
  });

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [products, setProducts] = useState<ProductInfo[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation(); 
  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };
   

  interface Errors {
    selectedProductId?: string;
    lotId?: string;
    manufactureDate?: string;
    expiryDate?: string;
    receivedGms?: string;
    numContainers?: string;
  }

  const [errors, setErrors] = useState<Errors>({});
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const res = await MediaSolutionService.getMediaSolutionProductDefinitions();
        if (res?.status && res?.data) {
          const formatted = res.data.map((item: any) => ({
            id: item.id,
            name: item.solution_product_name,
            manufacturer: item.manufacturer_name,
            sku: item.catalog_number,
          }));
          setProducts(formatted);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products", error);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);


  // Update manufacturer and SKU when a product is selected
  useEffect(() => {
    const product = products.find(p => p.id === formData.selectedProductId);
    if (product) {
      setProductData({
        manufacturer: product.manufacturer,
        sku: product.sku,
      });
    } else {
      setProductData({ manufacturer: '', sku: '' });
    }
  }, [formData.selectedProductId, products]);


  // Auto-calculate quantity per container based on total and count
  useEffect(() => {
    const total = parseFloat(formData.receivedGms.toString());
    const count = parseFloat(formData.numContainers.toString());

    if (!isNaN(total) && !isNaN(count) && count > 0) {
      setFormData(prev => ({
        ...prev,
        qtyPerContainer: (total / count).toFixed(2),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        qtyPerContainer: '0.00',
      }));
    }
  }, [formData.receivedGms, formData.numContainers]);


  
  const validateForm = () => {
    const newErrors: Errors = {};

    if (!formData.selectedProductId) newErrors.selectedProductId = t("solutionInventory.errors.product");
    if (!formData.lotId) newErrors.lotId = t("solutionInventory.errors.lotId");
    if (!formData.manufactureDate) newErrors.manufactureDate = t("solutionInventory.errors.manufactureDate");
    if (!formData.expiryDate) newErrors.expiryDate = t("solutionInventory.errors.expiryDate");
    if (!formData.receivedGms) {
        newErrors.receivedGms = t("solutionInventory.errors.receivedGms");
    } else if (Number(formData.receivedGms) <= 0) {
        newErrors.receivedGms = t('solutionInventory.errors.quantity');
    }
    if (!formData.numContainers) {
        newErrors.numContainers = t("solutionInventory.errors.numContainers");
    } else if (
        Number(formData.numContainers) <= 0 ||
        Number(formData.numContainers) > Number(formData.numContainers)) 
    {
        newErrors.numContainers =t('solutionInventory.errors.containerValidation');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };


  // Save handler to create new solution lot
  const handleSave = async () => {
    if (isSaving) return;
    if (!validateForm()) return;

    const payload = {
      solution_product_id: Number(formData.selectedProductId),
      supplier_lot_number: formData.lotId,
      total_volume_received: Number(formData.receivedGms),
      manufacturing_date: formData.manufactureDate,
      expiry_date: formData.expiryDate,
      no_of_units: Number(formData.numContainers),
      quantity_per_unit: Number(formData.qtyPerContainer),
    };


    try {
      setIsSaving(true);
      const res = await MediaSolutionService.createMediaSolution(payload);
      if (res?.status) {
        showToastMessage(res?.message, "success");
        onSave();
        return;
      } else {
        showToastMessage(res?.message, "error");
      }
    } catch (error) {
      showToastMessage(t("solutionInventory.errors.errorSaving"), "error");
    }
    finally {
      setIsSaving(false);
    }
  };


  return (
  <LogNewShipmentForm
    formData={formData}
      productData={productData}
      products={products}
      loadingProducts={loadingProducts}
      errors={errors}
      onChange={handleChange}
      onSave={handleSave}
      isSaving={isSaving}
      onCancel={onCancel}
      translationKey="solutionInventory"
    />
  );
};

export default PrepareSolution;
